import { type ReactElement, useMemo } from 'react'
import { CloseIcon } from '@dhl-ui/icons'
import type { BaseProps } from '../../types'
import { spacing } from '../../theme/spacing'
import { Body1, H6 } from '../Typography'
import { IconButton } from '../IconButton'
import { Button } from '../Button'
import { Box } from '../Box'
import { Stack } from '../Stack'
import { getIcon } from './get-icon'
import { useStyle } from './use-style'
import type { AlertProps } from './types'

export type InnerAlertProps = AlertProps &
  BaseProps<HTMLDivElement> & {
    // only exposed for non Alert version (for example PageAlert)
    isRounded?: boolean
    // only exposed for non Alert version (for example PageAlert)
    isDense?: boolean
    // only exposed for non Alert version (for example PageAlert)
    isSticky?: boolean
    // only exposed for non Alert version (for example PageAlert)
    icon?: ReactElement
  }

/**
 * InnerAlert is used in external components `Alert` and `PageAlert`. Some of these props are not allowed on the exposed Alert :)
 *
 * @private
 */
export const InnerAlert = ({
  severity = 'success',
  hasIcon = true,
  icon,
  title,
  children,
  variant,
  sx,
  action,
  onClose,
  isRounded = true,
  isDense = false,
  isSticky = false,
  ...props
}: InnerAlertProps) => {
  const baseStyle = useStyle({ severity, variant, isRounded, isDense, isSticky })
  const iconElement = hasIcon ? icon || getIcon(severity) : null

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...sx,
    }),
    [baseStyle, sx]
  )

  return (
    <Box {...props} className="DhlAlert" sx={style} data-severity={`alert-${severity}`} role="alert">
      <Stack
        direction="row"
        sx={{
          gap: 2,
        }}
      >
        {iconElement ? (
          <Box
            sx={{
              display: 'flex',
              fontSize: spacing(3),
              width: spacing(3),
              padding: spacing(1, 0),
              mt: `-${spacing(0.25)}`,
            }}
          >
            {iconElement}
          </Box>
        ) : null}

        <Box sx={{ flex: 1, py: 1 }}>
          {title && <H6 variant="inherit">{title}</H6>}

          <Body1 variant="inherit" component="div">
            {children}
          </Body1>

          {action ? (
            <Button
              variant="outlined"
              size="small"
              color={variant === 'filled' ? 'light' : 'inherit'}
              onClick={action.onClick}
              sx={{ mt: 1.5 }}
            >
              {action.label}
            </Button>
          ) : null}
        </Box>

        {onClose ? (
          <IconButton variant="ghost" square onClick={onClose} size="small" color="inherit" aria-label="Close">
            <CloseIcon />
          </IconButton>
        ) : null}
      </Stack>
    </Box>
  )
}
